<script lang="ts">
/**
 * 快捷入口一项。
 *
 * perm 缺省表示**不做权限过滤**（wms 首页的现状）。给了 perm 才按 can() 过滤。
 */
export interface QuickLink {
  /** 目标路由，例如 '/dispatch' */
  to: string
  /** 按钮文案 */
  label: string
  /** 权限码，例如 'dsp:task'；缺省则所有登录用户都能看到 */
  perm?: string
}
</script>

<script setup lang="ts">
import { computed } from 'vue'

import { can } from '../composables/usePermissions'

const props = defineProps<{
  links: readonly QuickLink[]
}>()

// 导航交给宿主，不用 vue-router：
// ui-kit 的 package.json 里刻意没有这个依赖（只有 AppShell/ProfilePage 这类纯展示组件），
// 各 app 的 Home.vue 自己就持有 router，在那边接 @navigate 即可。
const emit = defineEmits<{
  (e: 'navigate', to: string): void
}>()

/**
 * 权限过滤。
 *
 * 这里不能图省事写成 can(link.perm)：can() 对**空参数**返回 true、对 **[undefined]** 返回 false，
 * 于是任何一个漏写 perm 的入口都会被静默藏掉，现场表现是「权限怎么配都不显示」，
 * 比不过滤更难查。故显式短路：没写 perm = 不过滤。
 */
const visible = computed(() => props.links.filter((l) => !l.perm || can(l.perm)))
</script>

<template>
  <el-card>
    <template #header>
      <b>快捷入口</b>
    </template>
    <div class="yc-quick-links">
      <el-button v-for="link in visible" :key="link.to" @click="emit('navigate', link.to)">
        {{ link.label }}
      </el-button>
    </div>
  </el-card>
</template>

<style scoped lang="scss">
.yc-quick-links {
  // el-button 自带左右内边距，间距给 --yc-space-2 就够；再大就散成一排孤零零的按钮。
  // flex-wrap 让窄屏自然折行，而不是把内容区撑出横向滚动条。
  display: flex;
  flex-wrap: wrap;
  gap: var(--yc-space-2);
}
</style>
